import {
	genClassName,
	ImgTile,
	PaddedContainer,
	SPACING,
	Text,
} from "@disco/disco_core";
import React, { useState } from "react";
import useEssentialWidgetOffer from "../../hooks/useEssentialWidgetOffer";
import EssentialWidgetOfferAction from "./EssentialWidgetOfferAction";
import useDimensions from "../../hooks/useDimensions";
import useCustomization, {
	CUSTOMIZATION_DETAIL,
	CUSTOMIZATION_TYPE,
} from "../../hooks/useCustomization";

import "./essential-widget-offer.css";
import useData from "../../hooks/useData";
import useCachedViewTrack, {
	TRACKED_TARGET,
} from "../../hooks/useCachedViewTrack";
import { eventTypes } from "../../hooks/useTrack";
import { BRAND_CLICK_TARGET } from "../../event_conf";
import { SNOWPLOW_EVENT } from "../../utils/snowplowEventMigrator";
import useImpression from "../../hooks/useImpression";
import getImgUrl from "../../utils/getImgUrl";
import useDocVisibility from "../../hooks/useDocVisibility";
import useMode from "../../hooks/useMode";

const EssentialWidgetOfferHeader = () => {
	const { applyCustomizationOrFallback } = useCustomization();
	const { scrapedInformation: { first_name: firstName = "" } = {} } =
		useData() || {};

	const { isInline } = useMode();

	const getHeadingFallback = () => {
		return `${
			firstName ? `${firstName}, thank you` : "Thank you"
		} for your purchase:`;
	};

	const heading = applyCustomizationOrFallback(
		CUSTOMIZATION_DETAIL[CUSTOMIZATION_TYPE.CHECKOUT].WIDGET_HEADING,
		getHeadingFallback()
	);

	return (
		<Text
			vPadding={isInline ? 16 : 14}
			tightTop
			size={"body"}
			style={{ fontWeight: 700 }}
		>
			{heading}
		</Text>
	);
};

const EssentialWidgetOfferContent = () => {
	const { inDesktopOnly } = useDimensions();

	const { activeOffer, isFirstOffer } = useEssentialWidgetOffer();

	const MAX_LOGO_SIZE = 140;
	const {
		name: advertiserName,
		incentive_heading: offerHeading,
		incentive_subheading: offerSubheading,
	} = activeOffer;

	const renderImg = (
		<PaddedContainer
			marginBottom={!inDesktopOnly && SPACING.TINY}
			flexContent
			centerContent
		>
			<ImgTile
				src={getImgUrl(
					activeOffer,
					1.5 * MAX_LOGO_SIZE,
					1.5 * MAX_LOGO_SIZE,
					"logo_url"
				)}
				alt={`${advertiserName} Logo`}
				style={{
					"--size": `${MAX_LOGO_SIZE}px`,
					objectFit: "contain",
				}}
			/>
		</PaddedContainer>
	);

	const shouldRenderImgOnMobile = !isFirstOffer && !inDesktopOnly;
	const shouldRenderImgOnDesktop = !isFirstOffer && inDesktopOnly;

	return (
		<PaddedContainer
			className={genClassName({
				base: "essential-widget-offer-content",
				conditionals: {
					inDesktopOnly: "essential-widget-offer-content-large",
				},
			})}
			flexContent={inDesktopOnly}
		>
			{shouldRenderImgOnMobile && renderImg}
			<PaddedContainer
				style={{ flexGrow: 1 }}
				hPadding={shouldRenderImgOnDesktop ? SPACING.REGULAR : 0}
				tightLeft
			>
				<Text size={"body"} style={{ fontWeight: 700 }}>
					{offerHeading}
				</Text>
				<Text size={"body"}>{offerSubheading}</Text>
				<PaddedContainer marginTop={SPACING.REGULAR}>
					<EssentialWidgetOfferAction />
				</PaddedContainer>
			</PaddedContainer>
			{shouldRenderImgOnDesktop && renderImg}
		</PaddedContainer>
	);
};

const EssentialWidgetOffer = () => {
	const { activeOffer, isFirstOffer, defaultOfferEventProps } =
		useEssentialWidgetOffer();

	const { id: brandId } = activeOffer || {};

	const [brandContainerRef, setBrandContainerRef] = useState(null);
	const { docVisibilityState } = useDocVisibility();

	// Adding a loose check (!hidden instead of "is visible") to handle undefined scenario's
	const shouldTrackDisplay = docVisibilityState !== "hidden";
	const { isInline } = useMode();

	useCachedViewTrack({
		id: brandId,
		key: TRACKED_TARGET.BRAND,
		event: eventTypes.WIDGET_BRAND_DISPLAY,
		properties: {
			...defaultOfferEventProps,
			initiatingTarget: BRAND_CLICK_TARGET.INLINE_COVER_TILE,
		},
		canTrack: shouldTrackDisplay,
	});

	useImpression({
		id: brandId,
		cacheKey: TRACKED_TARGET.VIEWABLE_THRESHOLD_BRAND,
		event: SNOWPLOW_EVENT.WIDGET_VIEWABLE_THRESHOLD_BRAND_DISPLAY,
		properties: {
			...defaultOfferEventProps,
			initiatingTarget: BRAND_CLICK_TARGET.INLINE_COVER_TILE,
		},
		target: brandContainerRef,
		isActive: shouldTrackDisplay,
	});

	return (
		<PaddedContainer
			vPadding={isInline ? 16 : 6}
			tightTop
			key={brandId}
			ref={(ref) => setBrandContainerRef(ref)}
			data-intersectionuid={brandId} // for useImpression Tracking
		>
			{isFirstOffer && <EssentialWidgetOfferHeader />}
			<EssentialWidgetOfferContent />
		</PaddedContainer>
	);
};
export default EssentialWidgetOffer;
